import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { AuthCard } from '@shared/ui';
import { LoginForm, RegisterForm } from '@features/auth-flow';

type AuthMode = 'login' | 'register';

/**
 * Authentication Page
 * Uses specialized AuthCard for proper form display
 */
export function LoginPage() {
    const { t } = useTranslation();
    const [mode, setMode] = useState<AuthMode>('login');

    const toggleMode = () => {
        setMode(mode === 'login' ? 'register' : 'login');
    };

    return (
        <div className="min-h-screen w-full flex items-center justify-center bg-background px-4 py-12 relative">
            {/* Back to Home Button */}
            <div className="absolute top-4 left-4 sm:top-8 sm:left-8 animate-fade-in">
                <Link
                    to="/"
                    className="flex items-center gap-2 text-text-secondary hover:text-primary transition-colors font-mono text-sm"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m12 19-7-7 7-7" />
                        <path d="M19 12H5" />
                    </svg>
                    {t('auth.backToHome')}
                </Link>
            </div>

            <div className="w-full max-w-4xl">
                {/* Header */}
                <div className="text-center mb-8 animate-fade-in">
                    <Link to="/" className="inline-block mb-6">
                        <h1 className="text-4xl sm:text-5xl font-bold text-primary font-mono tracking-tight">
                            {t('app.name')}
                        </h1>
                        <p className="text-sm sm:text-base text-text-secondary mt-2">
                            {t('app.tagline')}
                        </p>
                    </Link>
                </div>

                {/* Auth Card with Forms */}
                <AuthCard>
                    {mode === 'login' ? (
                        <LoginForm onToggleMode={toggleMode} showToggle={true} />
                    ) : (
                        <RegisterForm onToggleMode={toggleMode} showToggle={true} />
                    )}
                </AuthCard>

                {/* Footer */}
                <div className="mt-8 text-center text-xs sm:text-sm text-text-muted font-mono">
                    <p>© 2026 DevLint. All rights reserved.</p>
                </div>
            </div>
        </div>
    );
}
